import * as React from 'react'
import { minutesToTime } from '../helpers/time'
import { WorkingDayStatusShape } from '../../api/api_data_types'
import {
  working_day_status_path,
  edit_working_day_status_path,
  new_working_day_status_path,
  working_day_statuses_path,
  presence_days_path,
  new_resources_absence_path,
  new_range_entry_path,
  new_resources_record_path,
  new_resources_adjustment_path,
} from '../../routes'
import { EditModeContext } from './EditModeContext'

type TotalRowProps = {
  total: { min: number; overtime_min: number; supplement_min: number }
  date: string
  canCreateEntry: boolean
  hasPresenceDay: boolean
  hasAbsenceDay: boolean
  hasAnyRecords: boolean
  canExportAny?: boolean
  workingDayStatus?: WorkingDayStatusShape
  optimisticTimeDelta?: number
  hasEditableRecords?: boolean
  isSticky?: boolean
}

function formatTotal(total: { min: number; overtime_min: number; supplement_min: number }): string {
  let out = ''
  if (total.min > 0) {
    out += minutesToTime(total.min)
  }
  if (total.overtime_min > 0) {
    if (out.length > 0) {
      out += ', '
    }
    out += `(${minutesToTime(total.overtime_min)})`
  }
  if (total.supplement_min > 0) {
    if (out.length > 0) {
      out += ', '
    }
    out += `[${minutesToTime(total.supplement_min)}]`
  }
  return out
}

function getStatusIconClass(status: string | null): string {
  if (status === 'travel_day') {
    return 'fa-globe'
  }
  return ''
}

function getStatusPath(workingDayStatus: WorkingDayStatusShape | undefined, status: string, date: string): { path: string; method: string; data?: any } {
  const params = { date, 'working_day_status[status]': status }
  
  if (workingDayStatus?.id && workingDayStatus.status === status) {
    return {
      path: working_day_status_path(workingDayStatus.id, params),
      method: 'delete',
      data: { confirm: 'Er du sikker på du vil slette?' }
    }
  }
  
  if (status === 'travel_day') {
    if (workingDayStatus?.id) {
      return {
        path: edit_working_day_status_path(workingDayStatus.id, params),
        method: 'get'
      }
    } else {
      return {
        path: new_working_day_status_path(params),
        method: 'get'
      }
    }
  }
  
  if (workingDayStatus?.id) {
    return {
      path: working_day_status_path(workingDayStatus.id, params),
      method: 'patch'
    }
  } else {
    return {
      path: working_day_statuses_path(params),
      method: 'post'
    }
  }
}

function getStatusTitle(status: string): string {
  if (status === 'travel_day') {
    return 'Rejsedag'
  }
  return status
}

export default function TotalRow({
  total,
  date,
  canCreateEntry,
  hasPresenceDay,
  hasAbsenceDay,
  hasAnyRecords,
  workingDayStatus,
  optimisticTimeDelta = 0,
  hasEditableRecords = false,
  isSticky = false,
}: TotalRowProps) {
  const editModeContext = React.useContext(EditModeContext)
  const isEditMode = editModeContext?.isEditMode === true
  const isSaving = editModeContext?.isSaving === true
  const toggleEditMode = editModeContext?.toggleEditMode

  const sentinelRef = React.useRef<HTMLDivElement>(null)
  const [isStuck, setIsStuck] = React.useState(false)

  React.useEffect(() => {
    if (!isSticky || !sentinelRef.current) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        // When sentinel is not intersecting (scrolled past), the row is stuck
        setIsStuck(!entry.isIntersecting)
      },
      { threshold: 0 }
    )

    observer.observe(sentinelRef.current)
    return () => observer.disconnect()
  }, [isSticky])

  const adjustedTotal = {
    min: total.min + optimisticTimeDelta,
    overtime_min: total.overtime_min,
    supplement_min: total.supplement_min,
  }
  const totalDisplay = formatTotal(adjustedTotal)
  const statuses = ['travel_day']

  const showButtons = !isSticky || !isStuck

  return (
    <>
      {isSticky && <div ref={sentinelRef} className="total-row-sentinel" />}
      <div className="row">
        <div className="col">
          <hr />
        </div>
      </div>
      <div className={`row total-row ${isSticky ? 'total-row-sticky' : ''} ${isStuck ? 'is-stuck' : ''}`}>
        <div className="col d-flex flex-wrap justify-content-end">
          {hasAnyRecords && totalDisplay && (
            <div className="total mr-auto d-flex align-items-center">
              <div className="total-value">I alt: {totalDisplay}</div>
              {hasEditableRecords && toggleEditMode && (
                <button
                  type="button"
                  className={`btn ml-2 ${isEditMode ? 'btn-secondary' : 'btn-outline-primary'}`}
                  onClick={toggleEditMode}
                  disabled={isSaving}
                  title="Rediger inline"
                  aria-label="Rediger inline"
                >
                  {isSaving ? <i className="fa fa-spinner fa-spin" /> : <i className="fa fa-edit" />}
                </button>
              )}
            </div>
          )}

          {showButtons && canCreateEntry && (
            <>
              <div className="mt-2 mt-md-0">
                {statuses.map(status => {
                  const iconClass = getStatusIconClass(status)
                  const { path, method, data } = getStatusPath(workingDayStatus, status, date)
                  const isActive = workingDayStatus?.status === status
                  const classes = ['btn', 'ml-1']
                  if (isActive) {
                    classes.push('btn-primary')
                  } else {
                    classes.push('btn-outline-secondary')
                  }
                  
                  return (
                    <a
                      key={status}
                      className={classes.join(' ')}
                      href={path}
                      data-method={method}
                      data-confirm={data?.confirm}
                      title={getStatusTitle(status)}
                    >
                      <span className={`fa ${iconClass}`}></span>
                    </a>
                  )
                })}
              </div>

              {!hasPresenceDay && (
                <a
                  className="btn btn-outline-secondary ml-2 mt-2 mt-md-0"
                  href={presence_days_path({ date })}
                  data-method="post"
                >
                  Arbejdsdag
                </a>
              )}
              {!hasAbsenceDay && (
                <a
                  className="ml-2 mt-2 mt-md-0 btn btn-outline-secondary"
                  href={new_resources_absence_path({ date })}
                >
                  Fravær
                </a>
              )}
              <a
                className="ml-2 mt-2 mt-md-0 btn btn-outline-secondary"
                href={new_range_entry_path()}
              >
                + Periode
              </a>
              <a
                className="ml-2 mt-2 mt-md-0 btn btn-outline-primary"
                href={new_resources_record_path({ date })}
              >
                + Tilføj
              </a>
            </>
          )}
          {showButtons && !canCreateEntry && (
            <a
              className="ml-2 mt-2 mt-md-0 btn btn-outline-primary"
              href={new_resources_adjustment_path({ date })}
            >
              Tilføj korrektion
            </a>
          )}
        </div>
      </div>
      <div className="row">
        <div className="col">
          <hr />
        </div>
      </div>
    </>
  )
}

